<template>
  <b-sidebar id="sidebar-generate-pix-installment-plan" sidebar-class="sidebar-lg" :visible="visible" bg-variant="white"
    shadow backdrop no-header right @change="val => $emit('update:visible', val)" @hidden="clear">
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h4 class="mb-0">Gerar Pix Parcelado</h4>
        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <b-form class="p-2" @submit.prevent="onSubmit">
        <p class="text-muted">
          Cria a transação de Pix Parcelado e a autorização de Pix Automático na Asaas. O cliente paga a primeira
          parcela pelo QR Code e autoriza o débito das seguintes.
        </p>

        <b-row>
          <b-col cols="12" class="mb-2">
            <b-form-group label="Valor total" label-for="pix-installment-amount">
              <b-form-input id="pix-installment-amount" v-model="amount" v-money="moneyConfig"
                :class="{ 'is-invalid': v$.amount.$error }" />
              <div class="invalid-feedback">
                <span v-if="v$.amount.required.$invalid || v$.amount.minValue.$invalid">
                  Você deve informar um valor válido maior que zero
                </span>
              </div>
            </b-form-group>
          </b-col>

          <b-col cols="12" class="mb-2">
            <b-form-group label="Parcelas" label-for="pix-installment-installments">
              <b-form-input id="pix-installment-installments" v-model="installments" type="number" min="1" max="60"
                :class="{ 'is-invalid': v$.installments.$error }" />
              <div class="invalid-feedback">
                <span v-if="v$.installments.required.$invalid || v$.installments.minValue.$invalid">
                  Você deve informar um valor válido maior que zero
                </span>
                <span v-else-if="v$.installments.maxValue.$invalid">
                  A quantidade de parcelas não pode passar de 60
                </span>
              </div>
            </b-form-group>
          </b-col>

          <b-col cols="12" class="mb-2">
            <b-form-group label="Vencimento da primeira parcela" label-for="pix-installment-first-date">
              <b-form-input id="pix-installment-first-date" v-model="firstInstallmentDate" type="date" :min="today"
                :class="{ 'is-invalid': v$.firstInstallmentDate.$error }" />
              <div class="invalid-feedback">
                <span v-if="v$.firstInstallmentDate.required.$invalid">
                  Você deve informar a data da primeira parcela
                </span>
              </div>
            </b-form-group>
          </b-col>

          <b-col v-if="installmentValue" cols="12">
            <small class="text-muted">
              {{ installments }}x de {{ installmentValue | toCurrency }}
            </small>
          </b-col>
        </b-row>

        <div class="d-flex mt-2">
          <b-button variant="primary" type="submit" :disabled="saving">
            {{ saving ? 'Gerando...' : 'Gerar cobrança' }}
          </b-button>
        </div>
      </b-form>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BButton, BCol, BRow, BFormInput,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import useVuelidate from '@vuelidate/core'
import { required, minValue, maxValue } from '@vuelidate/validators'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { VMoney } from 'v-money'
import { moneyToFloat, dateToLocalYMD } from '@/helpers/converters'
import * as types from '@/modules/contract/store/types'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
  },
  directives: {
    money: VMoney,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    contractId: {
      type: [String, Number],
      required: true,
    },
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() }
  },
  data() {
    return {
      saving: false,
      amount: undefined,
      installments: 1,
      firstInstallmentDate: dateToLocalYMD(new Date()),
      moneyConfig: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false,
      },
    }
  },
  computed: {
    today() {
      return dateToLocalYMD(new Date())
    },
    installmentValue() {
      const total = moneyToFloat(this.amount)
      const installments = parseInt(this.installments, 10)
      if (!total || !installments) {
        return undefined
      }
      return total / installments
    },
  },
  validations() {
    return {
      amount: {
        required,
        minValue: value => moneyToFloat(value) > 0,
      },
      installments: { required, minValue: minValue(1), maxValue: maxValue(60) },
      firstInstallmentDate: { required },
    }
  },
  methods: {
    ...mapActions({
      createPixInstallmentPlan: types.CREATE_PIX_INSTALLMENT_PLAN,
    }),
    clear() {
      this.amount = undefined
      this.installments = 1
      this.firstInstallmentDate = dateToLocalYMD(new Date())
      this.v$.$reset()
    },
    onSubmit() {
      this.v$.$touch()
      if (this.v$.$invalid) {
        return
      }

      this.saving = true
      this.createPixInstallmentPlan({
        id: this.contractId,
        amount: moneyToFloat(this.amount),
        installments: parseInt(this.installments, 10),
        first_installment_date: this.firstInstallmentDate,
      })
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso',
              text: 'Cobrança de Pix Parcelado gerada. O código Pix está disponível na listagem de transações.',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('saved')
          this.$emit('update:visible', false)
        })
        .catch(error => {
          const data = error?.response?.data
          // 422 de validação traz "errors" por campo e um "message" genérico;
          // o 422 de regra de negócio traz só "message".
          const message = Object.values(data?.errors || {})[0]?.[0]
            || data?.message
            || 'Ocorreu um erro ao gerar a cobrança. Entre em contato com o setor de TI.'
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!', text: message, icon: 'AlertTriangleIcon', variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>
